import type { ResourceCategoryKey } from '@/features/virtual-resource/constants'
import DataFileMap from '@/model/Resource/DataFileMap'

/**
 * 虚拟资源分类与引擎 mappingPath 规则。
 * 单一事实来源：改映射须同步 `docs/ue/引擎资源解析规则.md` 与 `资源管理器.md`。
 */
type ResourceTopFolderRule = {
  name: string
  mappingPath: string
  aliases?: string[]
  /** 轻量版资源管理器暂不展示该顶层目录，映射规则仍保留。 */
  hidden?: boolean
}

type ResourceCategoryRule = {
  label: string
  topFolders: ResourceTopFolderRule[]
}

const RESOURCE_RELATIVE_PATH_LINK_INDEX = 4
const RESOURCE_MAPPING_PATH_LINK_INDEX = 5

/** 四分类顶层文件夹 → 引擎 mappingPath；hidden 仅隐藏资源管理器入口，解析仍生效。 */
export const RESOURCE_CATEGORY_RULES: Record<ResourceCategoryKey, ResourceCategoryRule> = {
  graphic: {
    label: '图像',
    topFolders: [
      { name: '按钮', mappingPath: 'Graphics/button' },
      { name: '背景', mappingPath: 'Graphics/background' },
      { name: '动图', mappingPath: 'Graphics/oafs', hidden: true },
      { name: '立绘', mappingPath: 'Graphics/half' },
      { name: '气泡', mappingPath: 'Graphics/chat', hidden: true },
      { name: 'UI', mappingPath: 'Graphics/ui' },
      { name: '头像', mappingPath: 'Graphics/face', hidden: true },
      { name: '系统', mappingPath: 'Graphics/system', hidden: true },
      { name: '其他', mappingPath: 'Graphics/other', hidden: true },
    ],
  },
  audio: {
    label: '音频',
    topFolders: [
      { name: '音乐', mappingPath: 'Audio/bgm' },
      { name: '音效', mappingPath: 'Audio/se' },
      { name: '循环音效', mappingPath: 'Audio/bgs' },
      { name: '语音', mappingPath: 'Audio/voice' },
    ],
  },
  video: {
    label: '视频',
    topFolders: [{ name: '视频', mappingPath: 'Graphics/video' }],
  },
  font: {
    label: '字体',
    topFolders: [
      { name: '位图', mappingPath: 'Font', aliases: ['位图字体'] },
      { name: '文件', mappingPath: 'Font', aliases: ['字体文件'] },
    ],
  },
}

function joinSegments(...parts: Array<string | undefined>): string {
  return parts
    .map((part) => String(part || '').replace(/^\/+|\/+$/g, ''))
    .filter(Boolean)
    .join('/')
}

function splitSegments(path: string): string[] {
  return String(path || '')
    .split('/')
    .map((segment) => segment.trim())
    .filter(Boolean)
}

function normalizeSegment(value: string): string {
  return String(value || '').trim().toLowerCase()
}

function getTopFolderNames(rule: ResourceTopFolderRule): string[] {
  const mappingTail = rule.mappingPath.split('/').filter(Boolean).pop() ?? ''
  return [rule.name, ...(rule.aliases ?? []), mappingTail].filter(Boolean)
}

function mergeFolderChildren(target: DataFileMap, source: DataFileMap): void {
  if (target === source) return
  target.children.push(...source.children)
}

export function getResourceCategoryRule(category: ResourceCategoryKey): ResourceCategoryRule {
  return RESOURCE_CATEGORY_RULES[category]
}

/** 图像等分类下暂不展示的顶层虚拟目录（路径解析仍可用）。 */
export function isHiddenResourceTopFolder(category: ResourceCategoryKey, folderName: string): boolean {
  const rule = getTopFolderRuleBySegment(category, folderName)
  return rule?.hidden === true
}

export function getTopFolderRuleBySegment(
  category: ResourceCategoryKey,
  segment: string,
): ResourceTopFolderRule | null {
  const normalized = normalizeSegment(segment)
  if (!normalized) return null
  const rule = getResourceCategoryRule(category)
  return (
    rule.topFolders.find((item) =>
      getTopFolderNames(item).some((name) => normalizeSegment(name) === normalized),
    ) ?? null
  )
}

export function getTopFolderRuleByPath(
  category: ResourceCategoryKey,
  folderPath: string,
): ResourceTopFolderRule | null {
  const [firstSegment] = splitSegments(folderPath)
  return getTopFolderRuleBySegment(category, firstSegment ?? '')
}

export function getTopFolderRuleByMappingPath(
  category: ResourceCategoryKey,
  mappingPath: string,
): ResourceTopFolderRule | null {
  const normalized = String(mappingPath || '').trim()
  if (!normalized) return null
  const rule = getResourceCategoryRule(category)
  return rule.topFolders.find((item) => item.mappingPath === normalized) ?? null
}

export function isLockedTopLevelFolderPath(category: ResourceCategoryKey, folderPath: string): boolean {
  const segments = splitSegments(folderPath)
  return segments.length === 1 && getTopFolderRuleBySegment(category, segments[0] ?? '') !== null
}

export function canCreateSubfolderInPath(category: ResourceCategoryKey, folderPath: string): boolean {
  const segments = splitSegments(folderPath)
  if (!segments.length) return false
  return getTopFolderRuleBySegment(category, segments[0] ?? '') !== null
}

export function resolveVirtualResourcePathMeta(
  category: ResourceCategoryKey,
  folderPath: string,
  fileName: string,
): { relativePath: string; mappingPath: string } {
  const segments = splitSegments(folderPath)
  const topRule = getTopFolderRuleBySegment(category, segments[0] ?? '')
  const relativeSegments = topRule ? segments.slice(1) : segments
  return {
    relativePath: joinSegments(...relativeSegments, fileName),
    mappingPath: topRule?.mappingPath ?? '',
  }
}

export function resolveRelativePathFromVirtualPath(
  category: ResourceCategoryKey,
  virtualPath: string,
): { relativePath: string; mappingPath: string } {
  const segments = splitSegments(virtualPath)
  const fileName = segments.pop() ?? ''
  return resolveVirtualResourcePathMeta(category, joinSegments(...segments), fileName)
}

export function resolveEnginePathFromRelativePath(
  category: ResourceCategoryKey,
  mappingPath: string,
  relativePath: string,
): string {
  const relativeSegments = splitSegments(relativePath)
  const topRule = getTopFolderRuleByMappingPath(category, mappingPath)
  const topSegment = topRule?.mappingPath.split('/').filter(Boolean).pop() ?? ''
  return joinSegments(topSegment, ...relativeSegments)
}

export function resolveRelativePathFromEnginePath(
  category: ResourceCategoryKey,
  enginePath: string,
): { relativePath: string; mappingPath: string } {
  const segments = splitSegments(enginePath)
  const fileName = segments.pop() ?? ''
  return resolveVirtualResourcePathMeta(category, joinSegments(...segments), fileName)
}

export function resolveVirtualPathFromRelativePath(
  category: ResourceCategoryKey,
  mappingPath: string,
  relativePath: string,
): string {
  const relativeSegments = splitSegments(relativePath)
  const topRule = getTopFolderRuleByMappingPath(category, mappingPath)
  return joinSegments(topRule?.name ?? '', ...relativeSegments)
}

export function resolveVirtualFolderPathFromResourceMeta(
  category: ResourceCategoryKey,
  mappingPath: string,
  relativePath: string,
): string {
  const virtualPath = resolveVirtualPathFromRelativePath(category, mappingPath, relativePath)
  const segments = splitSegments(virtualPath)
  segments.pop()
  return joinSegments(...segments)
}

export function buildDefaultVirtualResourceRoot(category: ResourceCategoryKey): DataFileMap {
  const rule = getResourceCategoryRule(category)
  const root = DataFileMap.categoryRoot(rule.label)
  for (const folder of rule.topFolders) {
    if (folder.hidden) continue
    root.children.push(DataFileMap.categoryRoot(folder.name))
  }
  return root
}

export function ensureVirtualResourceRootStructure(
  category: ResourceCategoryKey,
  root: DataFileMap,
): DataFileMap {
  const rule = getResourceCategoryRule(category)
  root.name = rule.label

  const remaining = [...root.children]
  const orderedTopFolders: DataFileMap[] = []
  const matchedFolders = new Set<DataFileMap>()

  for (const folderRule of rule.topFolders) {
    const allowedNames = new Set(getTopFolderNames(folderRule))
    const matches = remaining.filter((child) => child.isFolder && allowedNames.has(child.name))
    const target = matches.shift() ?? DataFileMap.categoryRoot(folderRule.name)
    target.name = folderRule.name
    matchedFolders.add(target)
    for (const extra of matches) {
      matchedFolders.add(extra)
      mergeFolderChildren(target, extra)
    }
    orderedTopFolders.push(target)
  }

  const otherChildren = root.children.filter((child) => !matchedFolders.has(child))
  root.children = [...orderedTopFolders, ...otherChildren]
  return root
}

export function syncVirtualResourcePathMetadata(
  category: ResourceCategoryKey,
  root: DataFileMap,
): DataFileMap {
  ensureVirtualResourceRootStructure(category, root)

  function walk(folder: DataFileMap, folderSegments: string[]): void {
    folder.children = folder.children.filter((child) => child.isFolder || Boolean(child.resourceUrl.trim()))
    for (const child of folder.children) {
      if (child.isFolder) {
        walk(child, [...folderSegments, child.name])
        continue
      }
      const meta = resolveVirtualResourcePathMeta(category, joinSegments(...folderSegments), child.name)
      child.links[RESOURCE_RELATIVE_PATH_LINK_INDEX] = meta.relativePath
      child.links[RESOURCE_MAPPING_PATH_LINK_INDEX] = meta.mappingPath
    }
  }

  walk(root, [])
  return root
}
